import
{
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    List,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography
}
    from "@mui/material";
import { Add, Check, Close, Label, Schema, TextFields } from "@mui/icons-material";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { connect } from "react-redux";
import { selectConstants, selectSheets } from "../../redux/selectors/firebase";
import { Constants } from "../graph/StateTags";
import { FirebaseContext } from "../firebase/FirebaseProvider";
import makeStyles from "@mui/styles/makeStyles";
import { Sheets } from "./GridView";
import { noGroup } from "./gridViewFilters";
import { ColumnData, ColumnSourceType, ConstantColumnData, CustomColumnData, StateColumnData, stateDataTypes } from "./gridColumnDefs";

const columnDataTypes = {
    string: "String",
    number: "Number",
    date: "Date",
    dateTime: "Date & Time",
    boolean: "Boolean",
    singleSelect: "Select"
};

const useStyles = makeStyles(theme => ({
    list: {
        maxHeight: "300px",
        overflowY: "auto"
    },
    content: {

    },
    formControl: {
        width: "100%",
        padding: theme.spacing(1)
    },
    custom: {
        display: "flex",
        flexDirection: "row",
        flexBasis: "50%"
    },
    label: {
        left: "unset",
        top: "unset",
        backgroundColor: "white"
    }
}))

interface ColumnDetails
{
    id: string;
    columnType: ColumnSourceType;
    name: string;
    icon: JSX.Element;
}
export interface Props
{
    open: boolean
    close: () => void
    constants: Constants
    project: string,
    sheetId: string
    sheets: Sheets
}

function AddColumnDialog({ open, constants, close, sheetId, project, sheets }: Props)
{
    const customSource: ColumnDetails = useMemo(() => ({ id: "custom", columnType: "custom", name: "Custom", icon: <TextFields fontSize={"small"} /> }), []);
    const classes = useStyles();
    const { push } = useContext(FirebaseContext);

    const [name, setName] = useState("");
    const [type, setType] = useState("string");
    const [valueOptions, setValueOptions] = useState<string[]>([]);
    const [newValueOption, setNewValueOption] = useState<string>("");
    const [dataSource, setDataSource] = useState<string>(customSource.id);

    const sheet = useMemo(() => sheets[sheetId], [sheets, sheetId]);

    const existingColumns = useMemo(() =>
    {
        const sheetColumns = sheet?.columns ?? {};
        return Object.values(sheetColumns);
    }, [sheet]);

    const filterExistingColumn = useCallback(([id, data]: [string, any]) => {
        return id !== customSource.id && !existingColumns.find(column =>
            ((column.columnType === "constant" || column.columnType === "state") && column.source === id))
    },
    [existingColumns, customSource.id]);

    const dataSources: ColumnDetails[] = [customSource]
        .concat(Object.entries(stateDataTypes)
            .map(([id, column]) =>
                ({ id, columnType: "state", name: column.name, icon: <Schema fontSize={"small"} /> })))
        .concat(Object.entries(constants)
            .filter(filterExistingColumn)
            .map(([id, constant]) =>
                ({ id, columnType: "constant", name: constant.name, icon: <Label fontSize={"small"} /> })));

    const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) =>
    {
        setName(event.target.value);
    }

    const handleChangeType = (event: SelectChangeEvent) =>
    {
        setType(event.target.value as string);
    }

    const handleChangeDataSource = (event: SelectChangeEvent) =>
    {
        setDataSource(event.target.value);
    }

    const createColumn = useCallback((column: ColumnData) =>
    {
        return push(`sheets/${project}/${sheetId}/columns`, column);
    }, [push, project, sheetId]);

    const reset = useCallback(() =>
    {
        setName("");
        setType("string");
        setDataSource(customSource.id);
        setValueOptions([]);
        setNewValueOption("");
    }, [customSource.id]);

    const handleClickComplete = useCallback(() =>
    {
        const columnOrders = Object.values(sheet.columns ?? {}).map((column) => column.order).sort((a, b) => b - a);
        const order = (columnOrders.length > 0 ? (columnOrders[0] || 0) : (sheet.group && sheet.group !== noGroup.id) ? 1 : 0) + 1;
        const defaultWidth = 200;
        if (dataSource === customSource.id)
        {
            const column: CustomColumnData = {
                columnType: "custom",
                name,
                type,
                order,
                width: defaultWidth
            }

            createColumn(column).then((newColumnId) =>
            {
                if (newColumnId && type === "singleSelect")
                {
                    valueOptions.forEach((option) =>
                    {
                        push(`sheets/${project}/${sheetId}/columns/${newColumnId}`, option);
                    });
                }
            })
        }
        else
        {
            const columnDetails = dataSources.find((source) => source.id === dataSource);
            if (columnDetails)
            {
                const column: ConstantColumnData | StateColumnData = {
                    columnType: columnDetails.columnType as "constant" | "state",
                    source: dataSource,
                    width: defaultWidth,
                    order
                }
                createColumn(column);
            }
        }

        reset();
        close();
    }, [sheet, close, createColumn, customSource.id, dataSource, dataSources,
        name, reset, type, valueOptions, project, push, sheetId]);

    const handleClickCancel = () =>
    {
        reset();
        close();
    }

    const handleClose = () =>
    {
        reset();
        close();
    }

    const createDisabled = () =>
    {
        return dataSource === customSource.id && !Boolean(name) && type === "singleSelect" && valueOptions.length > 0;
    }

    const handleChangeNewValueOption = (event: React.ChangeEvent<HTMLInputElement>) =>
    {
        setNewValueOption(event.currentTarget.value);
    }

    const handleClickAddValueOption = (event: React.MouseEvent<HTMLElement>) =>
    {
        setValueOptions([...valueOptions, newValueOption]);
        setNewValueOption("");
    }

    return <Dialog open={open} onClose={handleClose} fullWidth>
        <DialogTitle>Add Column</DialogTitle>
        <DialogContent className={classes.content}>
            <FormControl fullWidth className={classes.formControl}>
                <InputLabel className={classes.label}>Data Source</InputLabel>
                <Select fullWidth value={dataSource} onChange={handleChangeDataSource}>
                    {dataSources.map(({ id, name, icon }) => <MenuItem key={id} value={id}>
                        {icon}<Typography component={"span"} style={{ marginLeft: "10px" }}>{name}</Typography>
                    </MenuItem>)}
                </Select>
            </FormControl>
            {dataSource === customSource.id && <Box className={classes.custom}>
                <FormControl className={classes.formControl}>
                    <TextField label={"Name"} fullWidth value={name} onChange={handleChangeName} />
                </FormControl>
                <FormControl className={classes.formControl}>
                    <InputLabel className={classes.label}>Data Type</InputLabel>
                    <Select fullWidth value={type} onChange={handleChangeType}>
                        {Object.entries(columnDataTypes).map(([type, typeName]) => <MenuItem key={type} value={type}>{typeName}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>}
            {type === "singleSelect" && <Box style={{ border: "1px solid #CCCCCC" }}>
                <List dense disablePadding className={classes.list}>
                    {valueOptions.map((option, index) => <MenuItem key={index}>{option}</MenuItem>)}
                </List>
                <Box style={{ display: "flex" }} >
                    <TextField label={"Add option"} fullWidth value={newValueOption} onChange={handleChangeNewValueOption} />
                    <IconButton onClick={handleClickAddValueOption} size={"small"}>
                        <Add />
                    </IconButton>
                </Box>
            </Box>}
            <DialogActions>
                <IconButton onClick={handleClickComplete} disabled={createDisabled()}>
                    <Check />
                </IconButton>
                <IconButton onClick={handleClickCancel}>
                    <Close />
                </IconButton>
            </DialogActions>
        </DialogContent>
    </Dialog >
}

const mapStateToProps = (store: never) => ({
    constants: selectConstants(store) ?? {},
    sheets: selectSheets(store) ?? {}
});
export default connect(mapStateToProps, {
})(AddColumnDialog);
